<template>
  <div>
    <h2>{{ i18n.t('/PurchaseOrder/Vestiging') }}</h2>
    <app-fieldset :title="i18n.t('/PurchaseOrder/Vestiging')" class="establishment">
      <div class="establishment-id-group">
        <loader v-if="loadingEstablishments" />
        <form-input
          v-if="readonlyEstablishmentId"
          id="establishment-id"
          type="text"
          :model-value="readonlyEstablishmentId"
          :title="i18n.t('/PurchaseOrder/VestigingID')"
          :readonly="true"
        />
        <app-form-field
          v-else
          id="name"
          class="establishment-id"
          :title="i18n.t('/PurchaseOrder/VestigingID')"
          :error-message="showError ? 'Selecteer een vestiging' : ''"
        >
          <autocomplete
            id="establishment-id"
            data-qa="dealer-option-item-input-field"
            :list="establishmentsForSelect"
            :model-value="selectedEstablishmentName"
            @change="updateEstablishment"
          />
        </app-form-field>

        <form-checkbox
          id="has-alternative-establishment-id"
          :title="i18n.t('/PurchaseOrder/AfleverenOpAlternatiefAfleveradres')"
        >
          <input
            id="has-alternative-establishment-id"
            v-model="hasAlternativeLocation"
            type="checkbox"
            @change="toggleAlternativeLocation"
          />
        </form-checkbox>
      </div>
      <loader v-if="loadingUnloadingLocations" />

      <grouped-autocomplete
        v-if="hasAlternativeLocation"
        id="alternative-establishment-id"
        :model-value="selectedUnloadingLocationName"
        class="alternative-establishment"
        :title="i18n.t('/PurchaseOrder/AlternatieveLoslocatie')"
        :option-groups="unloadingLocationsForSelect"
        :no-value-text="i18n.t('/PurchaseOrder/SelecteerLoslocatie')"
        @change="updateUnloadingLocation"
      />
    </app-fieldset>
  </div>
</template>

<script>
import AppFieldset from '@/components/generic/presentational/atoms/appFieldset/AppFieldset.vue'
import AppFormField from '@/components/generic/presentational/atoms/formField/FormField.vue'
import FormCheckbox from '@/components/generic/presentational/atoms/formCheckbox/FormCheckbox.vue'
import FormInput from '@/components/generic/presentational/atoms/formInput/FormInput.vue'
import StoreConnector from '@/helpers/StoreConnector'
import Autocomplete from '@/components/generic/presentational/molecules/autocomplete/Autocomplete.vue'
import GroupedAutocomplete from '@/components/generic/presentational/molecules/groupedAutocomplete/GroupedAutocomplete.vue'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import { useVuelidate } from '@vuelidate/core'

export default {
  components: {
    AppFieldset,
    FormCheckbox,
    FormInput,
    Loader,
    Autocomplete,
    GroupedAutocomplete,
    AppFormField,
  },
  directives: {
    focus: {
      inserted(el) {
        const search = el.querySelector('input[type=search]')
        if (search) search.focus()
      },
    },
  },
  props: {
    establishmentId: {
      type: String,
      default: () => '',
    },
    unloadingLocationId: {
      type: String,
      default: () => '',
    },
  },
  emits: ['change-unloading-location', 'change-establishment-id'],
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      hasAlternativeLocation: !!this.unloadingLocation,
      hasCustomer: false,
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      unloadingLocations: [],
      establishments: [],
      readonlyEstablishmentId: null,
      showError: false,
    }
  },
  computed: {
    loadingUnloadingLocations() {
      return !this.unloadingLocations
    },
    loadingEstablishments() {
      return !this.establishments
    },
    unloadingLocationsForSelect() {
      return [
        {
          name: this.i18n.t('/PurchaseOrder/Vestigingen'),
          options: this.getFilteredUnloadingLocationsForSelect(false),
        },
        {
          name: this.i18n.t('/PurchaseOrder/Loslocaties'),
          options: this.getFilteredUnloadingLocationsForSelect(true),
        },
      ]
    },
    establishmentsForSelect() {
      return this.establishments.map((establishment) => this.formatEstablishmentName(establishment))
    },
    selectedEstablishmentName() {
      const establishment = this.establishments.find((a) => a.Id == this.establishmentId)

      if (establishment != null) return this.formatEstablishmentName(establishment)

      return ''
    },
    selectedUnloadingLocationName() {
      const unloadingLocation = this.unloadingLocations.find(
        (a) => a.Id == this.unloadingLocationId
      )

      if (unloadingLocation != null) return this.formatUnloadingLocationName(unloadingLocation)

      return ''
    },
  },
  created() {
    StoreConnector.get('purchaseOrder/unloadingLocations', (data) => {
      this.unloadingLocations = data
    })

    StoreConnector.get('purchaseOrder/establishments', (data) => {
      this.establishments = data

      if (this.establishments && this.establishments.length === 1) {
        this.readonlyEstablishmentId = this.establishments[0].Id
        this.changeEstablishmentId(this.readonlyEstablishmentId)
      }
    })

    this.hasAlternativeLocation = !!this.unloadingLocationId

    StoreConnector.dispatch('purchaseOrder/getEstablishments')

    StoreConnector.dispatch('purchaseOrder/getUnloadingLocations')
  },
  updated() {
    if (this.selectedUnloadingLocationName.length > 0) {
      this.hasAlternativeLocation = true
    }
  },
  methods: {
    updateEstablishment(establishmentFormattedName) {
      if (establishmentFormattedName == null) return

      const foundEstablishment = this.establishments.find(
        (a) => this.formatEstablishmentName(a) == establishmentFormattedName
      )

      if (foundEstablishment != null) {
        this.showError = false
        this.changeEstablishmentId(foundEstablishment.Id)
      } else this.showError = true
    },
    updateUnloadingLocation(unloadingLocation) {
      if (!!unloadingLocation.value) {
        const fullLocation = this.unloadingLocations.find(
          (location) => location.Id === unloadingLocation.value
        )
        if (fullLocation) {
          const data = {
            ShippingLocationID: fullLocation.Id,
            DeliveredToDealerNumber: fullLocation.DeliveredToDealerNumber,
          }
          this.changeUnloadingLocation(data)
        } else this.changeUnloadingLocation(null)
      }
    },
    changeUnloadingLocation(data) {
      this.$emit('change-unloading-location', data)
    },
    changeEstablishmentId(data) {
      this.$emit('change-establishment-id', data)
    },
    getFilteredUnloadingLocationsForSelect(isAlternative) {
      return this.unloadingLocations
        .filter((location) => location.Alternative == isAlternative)
        .map((location) => {
          return {
            value: location.Id,
            text: `${location.Name}, ${location.Address}, ${location.ZipCode}, ${location.City}`,
          }
        })
    },
    toggleAlternativeLocation() {
      if (!this.hasAlternativeLocation) {
        this.changeUnloadingLocation(null)
      }
    },
    formatEstablishmentName(establishment) {
      return `${establishment.Id} - ${establishment.Name} - ${establishment.City}`
    },
    formatUnloadingLocationName(unloadingLocation) {
      return `${unloadingLocation.Name}, ${unloadingLocation.Address}, ${unloadingLocation.ZipCode}, ${unloadingLocation.City}`
    },
  },
}
</script>

<style lang="scss" scoped>
h2 {
  font-size: 1.46191rem;
  margin-bottom: var(--space-xxs);
}
</style>
